import { useCallback, useEffect, useMemo, useState } from 'react'
import { formatReal } from '@cannect/utils/formatters'
import { DEFAULT_SHIPPING_COST } from '@cannect/utils/constants'
import {
  TCartPreviewItem,
  TProgressiveApplication,
  TProgressiveTier,
  TRelatedProduct,
  isAmountTrigger,
  useCartProgressivePreview,
  useGetProductTiers,
  useProductRelatedByRule
} from '@cannect/services/resources/progressive-discounts'
import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import { pickProductCoverImagePath } from '@cannect/utils/productMedia'
import { ProgressiveGroupCard, ProgressiveGroupMilestone, ProgressiveGroupVariant } from './index'
import { ProgressiveGroupSheetCard } from './ProgressiveGroupSheetCard'
import { ProgressiveGroupOffer } from './ProgressiveGroupOffer'

// 'full' = carrinho amplo com linha RowFull (ex.: /carrinho-wa)
// 'full-sheetrow' = carrinho amplo, porém com a linha no layout do sheet (ex.: /carrinho)
// 'sheet' = drawer lateral compacto
type GroupMode = 'full' | 'full-sheetrow' | 'sheet'

interface GroupCardContainerProps {
  application: TProgressiveApplication
  cartItems: any[]
  incrementOrDecrementCart: (id: number, qty: number) => void | Promise<void>
  removeCart: (id: number) => void
  addToCart: (id: number, quantity?: number) => void | Promise<void>
  onValidityChange: (ruleId: number, ids: number[], valid: boolean) => void
  mode: GroupMode
  /** product_ids cujo desconto atual é do cupom (não mais progressivo) — saem do grupo, mas os demais ficam. */
  couponCoveredProductIds: Set<number>
}

function GroupCardContainer({
  application,
  cartItems,
  incrementOrDecrementCart,
  removeCart,
  addToCart,
  onValidityChange,
  mode,
  couponCoveredProductIds
}: GroupCardContainerProps) {
  const representativeProductId = application.eligible_items[0]?.product_id
  const { data: tiersData } = useGetProductTiers(representativeProductId)
  const { data: relatedData } = useProductRelatedByRule(
    representativeProductId,
    application.eligible_items.map((i) => i.product_id)
  )
  const [openVariantId, setOpenVariantId] = useState<number | null>(null)
  const [loadingVariantIds, setLoadingVariantIds] = useState<Set<number>>(new Set())

  // Só considera itens que ainda existem no carrinho AGORA e que NÃO foram cobertos pelo cupom.
  // application.eligible_items vem do preview (debounced 300ms) e fica temporariamente desatualizado
  // ao remover um item — sem isso, a linha removida renderiza com fallback "Produto :id" + imagem
  // padrão até o preview refazer (flash de placeholder). Cupom escopado: só sai do grupo quem o
  // cupom de fato cobriu — os demais produtos do mesmo grupo continuam no progressivo normalmente.
  const liveEligibleItems = application.eligible_items.filter(
    (i) =>
      cartItems.some((ci) => Number(ci?.product_id || ci?.product?.id || ci?.id) === i.product_id) &&
      !couponCoveredProductIds.has(i.product_id)
  )

  const addVariantLoading = useCallback((id: number) => {
    setLoadingVariantIds((prev) => new Set([...prev, id]))
  }, [])

  const removeVariantLoading = useCallback((id: number) => {
    setLoadingVariantIds((prev) => {
      const s = new Set(prev)
      s.delete(id)
      return s
    })
  }, [])

  // handleAdd: async para suportar loading gerenciado dentro de ProgressiveGroupOffer
  const handleAdd = useCallback(
    async (productId: number) => {
      await addToCart(productId, 1)
    },
    [addToCart]
  )

  // Reporta validade ao pai quando tiersData carregar — agrupa por count_mode=aggregated,
  // independente do benefit_target (item, shipping ou both viram card de grupo), e exige 2+ itens
  // AINDA no carrinho agora (não a contagem stale do preview) — senão o item removido fica preso
  // no Set de excluídos do pai mesmo depois de sumir do carrinho.
  useEffect(() => {
    if (!tiersData) return
    const isValid =
      !!tiersData.has_progressive_discount &&
      tiersData.rule?.count_mode === 'aggregated' &&
      liveEligibleItems.length >= 2
    onValidityChange(
      application.rule_id,
      liveEligibleItems.map((i) => i.product_id),
      isValid
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiersData, cartItems, couponCoveredProductIds])

  // Fecha dropdown ao clicar fora de elementos [data-dd]
  useEffect(() => {
    if (openVariantId === null) return undefined
    const handleDown = (e: MouseEvent) => {
      if (!(e.target as Element).closest('[data-dd]')) {
        setOpenVariantId(null)
      }
    }
    document.addEventListener('mousedown', handleDown)
    return () => document.removeEventListener('mousedown', handleDown)
  }, [openVariantId])

  // Degradação graciosa: sem dados, sem card
  if (!tiersData?.has_progressive_discount) return null
  // Qualquer benefit_target agrupa (item, shipping ou both) — desde que a faixa some as quantidades
  if (tiersData.rule?.count_mode !== 'aggregated') return null

  const { rule } = tiersData
  // Regra SÓ de frete: item não muda de preço — milestones/badge usam shipping_value, não discount_percent
  const isShippingOnly = rule?.benefit_target === 'shipping'
  const triggerType = rule?.trigger_type
  const isAmount = isAmountTrigger(triggerType)

  const allTiers = tiersData.tiers ?? []
  const displayTiers = allTiers
    .filter((t) => (isShippingOnly ? Number(t.shipping_value) > 0 : Number(t.discount_percent) > 0))
    .sort((a, b) => a.min_quantity - b.min_quantity)

  if (!displayTiers.length) return null
  // Menos de 2 itens ainda no carrinho: não é mais um grupo — deixa de renderizar (some instantaneamente)
  if (liveEligibleItems.length < 2) return null

  // Quantidade total do grupo em unidades (sempre usado para controles de qtd)
  const groupQty = liveEligibleItems.reduce((acc, i) => acc + i.quantity, 0)

  // Rótulo em minúscula para uso em meio de frase (groupOff, milestones, nextHint).
  const shippingTierLabel = (t: TProgressiveTier): string => {
    const v = Number(t.shipping_value) || 0
    if (t.shipping_discount_type === 'value') {
      return v >= DEFAULT_SHIPPING_COST
        ? 'custo de importação isento'
        : `${(formatReal(v) as string) || `R$ ${v}`} no custo de importação`
    }
    return v >= 100 ? 'custo de importação isento' : `${t.shipping_value}% no custo de importação`
  }

  // Para amount, o gatilho é a soma em R$ do grupo (qty × preço da linha de cada item).
  // Repete o lookup de `variantPrices` em vez de reusar `groupFullTotal` porque aquele bloco
  // depende de `activePct` — que vem da faixa ativa, que depende deste total.
  //
  // Só campos já em REAIS entram na conta: `unitPrice` (cn_items.unitPrice é gravado com a taxa de
  // cn_exchanges aplicada) e `real_price` (a API converte USD × taxa). `product.price` fica de fora
  // de propósito — é o valor na moeda de cadastro, e num importado vem em dólar; o item do carrinho
  // não traz `currency` para desambiguar. Faltando os dois, o item entra com 0: subestimar o
  // progresso é melhor que anunciar uma faixa que não existe, porque quem concede o desconto é o
  // backend, e ele soma este mesmo `unitPrice` (progressiveDiscountService.previewCart).
  const groupTriggerTotal = isAmount
    ? liveEligibleItems.reduce((acc, eligibleItem) => {
        const cartItem = cartItems.find((ci) => {
          const pid = Number(ci?.product_id || ci?.product?.id || ci?.id)
          return pid === eligibleItem.product_id
        })
        const linePriceBRL = Number(cartItem?.unitPrice || cartItem?.product?.real_price || 0)
        return acc + eligibleItem.quantity * linePriceBRL
      }, 0)
    : groupQty

  const maxTierMin = displayTiers[displayTiers.length - 1]?.min_quantity ?? 1

  // Faixa ativa pelo total do grupo: maior tier com min_quantity <= groupTriggerTotal
  const activeTier = displayTiers.filter((t) => Number(t.min_quantity) <= groupTriggerTotal).pop()

  // Percentual da faixa ativa sobre o ITEM (da API) — 0 quando a regra é só frete (item não baixa de preço)
  const activePct = activeTier ? Number(activeTier.discount_percent) || 0 : 0

  const appDiscountPct = application.tier?.discount_percent ?? activePct
  const groupOff = isShippingOnly && activeTier ? shippingTierLabel(activeTier) : `${appDiscountPct}% OFF`

  const progressPct = Math.min(100, (Math.min(groupTriggerTotal, maxTierMin) / maxTierMin) * 100)

  // Faixa progressiva realmente atingida (reduz o preço do item) — quando não atingida, cada
  // variante pode ainda cair para o preço promocional (ver Passo 1 abaixo).
  const isProgressiveActive = !isShippingOnly && activePct > 0

  const milestones: ProgressiveGroupMilestone[] = displayTiers.map((t) => {
    // Rotulo compacto, nao o de `formatTierQuantityLabel`: o chip da barra de progresso nao
    // acomoda "R$ 2.000,00 ou mais em produtos" sem quebrar a linha.
    const isOpenEnded = t.open_ended || t.min_quantity >= maxTierMin
    const suffix = isOpenEnded ? '+' : ''
    const thresholdLabel = isAmount
      ? `${formatReal(Number(t.min_quantity)) || `R$ ${t.min_quantity}`}${suffix}`
      : `${t.min_quantity}${suffix} un`
    const benefitLabel = isShippingOnly ? shippingTierLabel(t) : `${t.discount_percent}%`
    return { label: `${thresholdLabel} · ${benefitLabel}`, reached: groupTriggerTotal >= Number(t.min_quantity) }
  })

  const nextTier = displayTiers.find((t) => Number(t.min_quantity) > groupTriggerTotal)
  let nextHint = 'faixa máxima atingida'
  if (nextTier) {
    if (isAmount) {
      const missingR$ = nextTier.min_quantity - groupTriggerTotal
      const missingFmt = (formatReal(missingR$) as string) || `R$ ${missingR$.toFixed(2)}`
      nextHint = isShippingOnly
        ? `faltam ${missingFmt} para ${shippingTierLabel(nextTier)}`
        : `faltam ${missingFmt} para ${nextTier.discount_percent}% OFF`
    } else {
      const missingQty = nextTier.min_quantity - groupQty
      nextHint = isShippingOnly
        ? `faltam ${missingQty} un para ${shippingTierLabel(nextTier)}`
        : `faltam ${missingQty} un para ${nextTier.discount_percent}% OFF`
    }
  }

  // Passo 1: calcular base e preço final por variação (sobre o preço real da linha, não catálogo da API)
  const variantPrices = liveEligibleItems.map((eligibleItem) => {
    const cartItem = cartItems.find((ci) => {
      const pid = Number(ci?.product_id || ci?.product?.id || ci?.id)
      return pid === eligibleItem.product_id
    })
    // Preço base da LINHA do carrinho — ex.: base 100, faixa 20% → unitFinal 80,00
    const base = Number(cartItem?.unitPrice || cartItem?.product?.real_price || cartItem?.product?.price || 0)
    // Promoção do item — só entra em jogo quando a faixa progressiva do grupo ainda não foi
    // atingida (mesma precedência do carrinho: progressivo ativo > promoção > preço normal).
    const promotionalPrice = Number(cartItem?.promotion?.promotional_price || 0)
    const hasPromotion = promotionalPrice > 0 && promotionalPrice < base
    let unitFinal = base
    if (isProgressiveActive) {
      unitFinal = Number((base * (1 - activePct / 100)).toFixed(2))
    } else if (hasPromotion) {
      unitFinal = promotionalPrice
    }
    return { cartItem, eligibleItem, base, unitFinal, qty: eligibleItem.quantity, hasPromotion }
  })

  // Passo 2: totais e economia calculados sobre os preços reais
  const groupTotal = variantPrices.reduce((acc, v) => acc + v.unitFinal * v.qty, 0)
  const groupFullTotal = variantPrices.reduce((acc, v) => acc + v.base * v.qty, 0)
  const computedSavings = groupFullTotal - groupTotal
  const hasSavings = computedSavings > 0.005
  const savingsFmt = hasSavings ? (formatReal(computedSavings) as string) || undefined : undefined
  const groupTotalFmt = (formatReal(groupTotal) as string) || 'R$ --'

  // Passo 3: construir variants com preços relativos à base da linha
  const variants: ProgressiveGroupVariant[] = variantPrices.map(
    ({ cartItem, eligibleItem, base, unitFinal, qty, hasPromotion }) => {
      const itemProductId = Number(cartItem?.product_id || cartItem?.product?.id || eligibleItem.product_id)
      const name = cartItem?.product?.translated_name || cartItem?.product?.name || `Produto ${eligibleItem.product_id}`

      // Sabor: product.flavor já é string conforme API — não acessar .name
      const rawFlavor = cartItem?.product?.flavor
      const flavor = typeof rawFlavor === 'string' ? rawFlavor : (rawFlavor?.name ?? '')

      const hasDiscount = isProgressiveActive || hasPromotion
      // Percentual exibido no badge da variante: progressivo (faixa do grupo) quando ativo,
      // senão o percentual real da promoção (derivado do antes/depois, igual ao padrão de cupom).
      let variantDiscountPct: number | undefined
      if (isProgressiveActive) {
        variantDiscountPct = activePct
      } else if (hasPromotion && base > 0) {
        variantDiscountPct = Math.round(((base - unitFinal) / base) * 100)
      }
      const unitFmt = (formatReal(unitFinal) as string) || 'R$ --'
      const fullUnitFmt = hasDiscount && base > 0 ? (formatReal(base) as string) || undefined : undefined
      const totalFmt = (formatReal(unitFinal * qty) as string) || 'R$ --'
      const imageUrl = pickProductCoverImagePath(cartItem?.product?.media)

      return {
        id: itemProductId,
        qty,
        name,
        flavor,
        mainProductId: Number(cartItem?.product?.main_product_id) || undefined,
        imageUrl,
        tiers: displayTiers,
        isShipping: isShippingOnly,
        triggerType,
        unitFmt,
        fullUnitFmt,
        unitFinal,
        baseUnit: base,
        hasDiscount,
        discountPct: variantDiscountPct,
        totalFmt,
        isOpen: openVariantId === itemProductId,
        isLoading: loadingVariantIds.has(itemProductId),
        onToggle: () => setOpenVariantId((prev) => (prev === itemProductId ? null : itemProductId)),
        onRemove: () => removeCart(itemProductId),
        onSelect: async (n: number) => {
          if (loadingVariantIds.has(itemProductId)) return
          addVariantLoading(itemProductId)
          try {
            await incrementOrDecrementCart(itemProductId, n)
          } finally {
            removeVariantLoading(itemProductId)
            setOpenVariantId(null)
          }
        }
      }
    }
  )

  const subtitle =
    rule?.count_mode === 'aggregated'
      ? `${liveEligibleItems.length} sabores · desconto pela soma das quantidades`
      : undefined

  // Produtos relacionados à mesma regra ainda fora do carrinho (oferta cruzada)
  const cartProductIds = new Set(cartItems.map((ci) => Number(ci?.product_id || ci?.product?.id || ci?.id)))
  const eligibleProductIds = new Set(liveEligibleItems.map((i) => i.product_id))
  const offerProducts: TRelatedProduct[] = (relatedData?.products ?? []).filter(
    (p) => !cartProductIds.has(p.id) && !eligibleProductIds.has(p.id)
  )

  const cardProps = {
    groupTitle: application.rule_name,
    subtitle,
    groupOff,
    groupQty,
    nextHint,
    progressPct,
    milestones,
    variants,
    hasSavings,
    savingsFmt,
    groupTotalFmt,
    offerProducts: offerProducts.length > 0 ? offerProducts : undefined,
    onAdd: offerProducts.length > 0 ? handleAdd : undefined,
    groupDiscountPct: activePct > 0 ? activePct : undefined
  }

  if (mode === 'sheet') {
    return <ProgressiveGroupSheetCard {...cardProps} />
  }
  return <ProgressiveGroupCard {...cardProps} variantRow={mode === 'full-sheetrow' ? 'sheet' : 'full'} />
}

interface SingleOfferContainerProps {
  application: TProgressiveApplication
  cartItems: any[]
  addToCart: (id: number, quantity?: number) => void | Promise<void>
  mode: GroupMode
  /** Quando igual ao productId deste container, abre a oferta automaticamente. */
  qtyChangedProductId?: number
  /** product_ids cujo desconto atual é do cupom (não mais progressivo). */
  couponCoveredProductIds: Set<number>
}

function SingleOfferContainer({
  application,
  cartItems,
  addToCart,
  mode,
  qtyChangedProductId,
  couponCoveredProductIds
}: SingleOfferContainerProps) {
  const productId = application.eligible_items[0]?.product_id
  const { data: tiersData } = useGetProductTiers(productId)
  const { data: relatedData } = useProductRelatedByRule(
    productId,
    application.eligible_items.map((i) => i.product_id)
  )
  const [openSignal, setOpenSignal] = useState(0)

  // Abre a oferta quando o produto deste container teve quantidade alterada
  useEffect(() => {
    if (qtyChangedProductId === productId) {
      setOpenSignal((s) => s + 1)
    }
  }, [qtyChangedProductId, productId])

  const handleAdd = useCallback(
    async (pid: number) => {
      await addToCart(pid, 1)
    },
    [addToCart]
  )

  if (!tiersData?.has_progressive_discount) return null
  if (tiersData.rule?.count_mode !== 'aggregated') return null
  // Produto coberto pelo cupom: progressivo não se aplica mais a ele — sem oferta cruzada
  if (couponCoveredProductIds.has(productId)) return null

  const cartProductIds = new Set(cartItems.map((ci) => Number(ci?.product_id || ci?.product?.id || ci?.id)))
  const offerProducts: TRelatedProduct[] = (relatedData?.products ?? []).filter((p) => !cartProductIds.has(p.id))

  if (!offerProducts.length) return null

  // Resolve o desconto ativo para mostrar preço correto na oferta (maior faixa com qty <= 1)
  const displayTiers = (tiersData.tiers ?? [])
    .filter((t) => Number(t.discount_percent) > 0)
    .sort((a, b) => a.min_quantity - b.min_quantity)
  const activePct = Number(displayTiers[0]?.discount_percent) || 0

  return (
    <ProgressiveGroupOffer
      products={offerProducts}
      mode={mode === 'sheet' ? 'sheet' : 'full'}
      onAdd={handleAdd}
      openSignal={openSignal}
      groupDiscountPct={activePct > 0 ? activePct : undefined}
    />
  )
}

/** Retorna o Set de product_ids de grupos válidos (2+ eligible_items). */
export function getGroupedProductIds(applications?: TProgressiveApplication[]): Set<number> {
  if (!applications?.length) return new Set()
  return new Set(
    applications.filter((a) => a.eligible_items.length >= 2).flatMap((a) => a.eligible_items.map((i) => i.product_id))
  )
}

interface ProgressiveGroupSectionProps {
  cartItems: TCartPreviewItem[]
  /** Chamado sempre que o conjunto de product_ids confirmados em grupos muda. */
  onGroupedIdsChange?: (ids: Set<number>) => void
  /** 'full' = carrinho amplo (grid 5 colunas); 'sheet' = drawer lateral (layout vertical). */
  mode?: GroupMode
  /** ID do produto cuja quantidade foi alterada — abre oferta cross-sell de item único correspondente. */
  qtyChangedProductId?: number
}

/**
 * Seção de grupos de desconto progressivo.
 * mode='full' (default): carrinho amplo com grid de 5 colunas.
 * mode='sheet': drawer lateral com layout vertical compacto.
 * Reporta os IDs confirmados via onGroupedIdsChange para que o pai filtre a lista normal.
 * Retorna null quando não há dados (degradação graciosa).
 */
export function ProgressiveGroupSection({
  cartItems,
  onGroupedIdsChange,
  mode = 'full',
  qtyChangedProductId
}: ProgressiveGroupSectionProps) {
  const { data: previewData } = useCartProgressivePreview(cartItems)
  const { lastOrderCreated, incrementOrDecrementCart, removeCart, addToCart, coupon } = usePrepareCart()
  const allCartItems: any[] = lastOrderCreated?.items || []

  // Cupom aplicado cobre SÓ os produtos que ele de fato desconta — o backend expõe isso via
  // item.discount_type por item da order (itemOutputDTO). item.discount_type === 'progressive'
  // significa que esse item específico manteve o progressivo; qualquer outro valor truthy (ex.:
  // 'coupon') significa que o cupom tomou esse item. Os demais grupos/produtos não são afetados.
  const couponCoveredProductIds = useMemo(() => {
    if (!coupon) return new Set<number>()
    const ids = new Set<number>()
    allCartItems.forEach((ci: any) => {
      const pid = Number(ci?.product_id || ci?.product?.id || ci?.id)
      if (ci?.discount_type && ci.discount_type !== 'progressive') ids.add(pid)
    })
    return ids
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupon, allCartItems])

  // Grupos completos: 2+ itens elegíveis → card de grupo
  const groupApplications = useMemo(
    () => (previewData?.applications ?? []).filter((a) => a.eligible_items.length >= 2),
    [previewData?.applications]
  )

  // Singles: exatamente 1 item elegível → apenas oferta de cross-sell (não oculta item da lista normal)
  const singleApplications = useMemo(
    () => (previewData?.applications ?? []).filter((a) => a.eligible_items.length === 1),
    [previewData?.applications]
  )

  // Map rule_id → valid (confirmado por GroupCardContainer após tiersData carregar)
  const [validGroupMap, setValidGroupMap] = useState<Map<number, boolean>>(new Map())

  const handleValidityChange = useCallback((ruleId: number, ids: number[], valid: boolean) => {
    setValidGroupMap((prev) => {
      const next = new Map(prev)
      next.set(ruleId, valid)
      return next
    })
  }, [])

  // Propaga apenas IDs de groupApplications confirmados (singleApplications não ocultam itens).
  // Também filtra pelo carrinho AGORA (allCartItems) e pelos cobertos pelo cupom — sem isso, um item
  // recém-removido (ou coberto pelo cupom) continua marcado como "agrupado" pelo pai (usando o
  // eligible_items stale do preview) e some da lista normal também, ficando invisível em qualquer lugar.
  useEffect(() => {
    if (!onGroupedIdsChange) return
    const liveCartProductIds = new Set(allCartItems.map((ci) => Number(ci?.product_id || ci?.product?.id || ci?.id)))
    const validIds = new Set<number>()
    groupApplications.forEach((app) => {
      if (validGroupMap.get(app.rule_id) === true) {
        app.eligible_items.forEach((i) => {
          if (liveCartProductIds.has(i.product_id) && !couponCoveredProductIds.has(i.product_id)) {
            validIds.add(i.product_id)
          }
        })
      }
    })
    onGroupedIdsChange(validIds)
  }, [validGroupMap, groupApplications, allCartItems, couponCoveredProductIds, onGroupedIdsChange])

  if (!groupApplications.length && !singleApplications.length) return null

  return (
    <div>
      {groupApplications.map((app) => (
        <GroupCardContainer
          key={app.rule_id}
          application={app}
          cartItems={allCartItems}
          incrementOrDecrementCart={incrementOrDecrementCart}
          removeCart={removeCart}
          addToCart={addToCart}
          onValidityChange={handleValidityChange}
          mode={mode}
          couponCoveredProductIds={couponCoveredProductIds}
        />
      ))}
      {singleApplications.map((app) => (
        <SingleOfferContainer
          key={app.rule_id}
          application={app}
          cartItems={allCartItems}
          addToCart={addToCart}
          mode={mode}
          qtyChangedProductId={qtyChangedProductId}
          couponCoveredProductIds={couponCoveredProductIds}
        />
      ))}
    </div>
  )
}
